import { type Point, point } from '../../../elements';
import { vector } from '../../../elements/vector';
import { ICON_DEFAULT_FOREGROUND_COLOR } from '../../../graphConstants';
import { SvgPathBuilder } from '../../../svg/SvgPathBuilder';
import { makeTag } from '../../../svg/utils';
import {
  type NodeIconOptions,
  type NodeTypeIcon,
  nodeIconDefaultOptions,
} from '../NodeTypeIcon';
import {
  createSquareNodeTypeIcon,
  getArrowSvgPath,
  wrapNodeIconInSvgTag,
} from '../common';
import { drawSquareBackground } from '../drawIconBackground';

const ICON_SIZE = 40;
const ARROW_INNER_OFFSET = 12.4;
const ARROW_OUTER_OFFSET = 4.4;

function createUnknownIcon(options: NodeIconOptions = {}): string {
  const opts = { ...nodeIconDefaultOptions, ...options };

  const inner = {
    first: ARROW_INNER_OFFSET,
    second: ICON_SIZE - ARROW_INNER_OFFSET,
  };
  const outer = {
    first: ARROW_OUTER_OFFSET,
    second: ICON_SIZE - ARROW_OUTER_OFFSET,
  };

  const drawArrow = (coordX: 'first' | 'second', coordY: 'first' | 'second') =>
    makeTag('path', {
      d: getArrowSvgPath({
        from: point.create(inner[coordX], inner[coordY]),
        to: point.create(outer[coordX], outer[coordY]),
        headWidth: 9.2,
        headHeight: 4.8,
        tailWidth: 3.6,
      }),
      fill: ICON_DEFAULT_FOREGROUND_COLOR,
    });

  const createQuestionMarkPath = (
    size: number,
    strokeWidth: number,
    at: Point,
  ): string =>
    new SvgPathBuilder()
      .moveTo(vector.add(at, point.create(strokeWidth / 2, 0.36 * size)))
      .setScale(size)
      .relative()
      .cubicBezierTo(0, -0.11)(0.01, -0.16)(0.07, -0.22)
      .cubicBezierTo(0.06, -0.08)(0.15, -0.09)(0.24, -0.07)
      .cubicBezierTo(0.09, 0.02)(0.19, 0.1)(0.19, 0.22)
      .cubicBezierTo(0.0, 0.12)(-0.09, 0.16)(-0.13, 0.19)
      .cubicBezierTo(-0.06, 0.02)(-0.09, 0.05)(-0.09, 0.09)
      .vLineTo(0.17)
      .moveTo(0, 0.1)
      .setScale(1)
      .vLineTo(strokeWidth)
      .build();

  const questionStrokeWidth = 2;
  const icon = `
    ${drawSquareBackground(ICON_SIZE, opts.style)}
    ${drawArrow('first', 'first')}
    ${drawArrow('first', 'second')}
    ${drawArrow('second', 'first')}
    ${drawArrow('second', 'second')}
    ${makeTag('path', {
      d: createQuestionMarkPath(
        12.8,
        questionStrokeWidth,
        point.create(16, 12.8),
      ),
      fill: 'none',
      stroke: ICON_DEFAULT_FOREGROUND_COLOR,
      'stroke-width': questionStrokeWidth,
    })}
  `;

  return wrapNodeIconInSvgTag(icon, ICON_SIZE, ICON_SIZE, 'topLeft', opts);
}

export const unknownIcon: NodeTypeIcon = createSquareNodeTypeIcon(
  ICON_SIZE,
  createUnknownIcon,
);
